type Dict = Record<string, unknown>;

// A type alias (not an interface) so it is assignable to Dict for extend().
type ObjectUtils = {
  extend<A, B>(target: A, source: B): A & B;
  extend<A, B, C>(target: A, b: B, c: C): A & B & C;
  extend<A, B>(deep: boolean, target: A, source: B): A & B;
  extend(...args: unknown[]): unknown;
  isArrayLike(obj: unknown): boolean;
  type(obj: unknown): string;
  isFunction(obj: unknown): obj is (...args: unknown[]) => unknown;
  // A real Date has class "date", so this is true only for plain objects with toUTCString.
  isDate(obj: unknown): obj is Date;
  isArray(obj: unknown): obj is unknown[];
  isWindow(obj: unknown): obj is Window;
  isNumeric(obj: unknown): boolean;
  isString(obj: unknown): obj is string;
  isPlainObject(obj: unknown): obj is Dict;
  isEmptyObject(obj: object): boolean;
  each<T extends ArrayLike<unknown>>(
    obj: T,
    callback: (this: unknown, ...args: unknown[]) => unknown,
    args?: unknown[],
  ): T;
};

const utils = {} as ObjectUtils;

export default utils;

const class2type: Record<string, string> = {};
const objectToString = class2type.toString;
const hasOwn = class2type.hasOwnProperty;

// Slolen from jQuery
utils.extend = function (): unknown {
  let options: Dict | null,
    name: string,
    src: unknown,
    copy: unknown,
    copyIsArray: boolean | undefined,
    clone: unknown,
    target: Dict = arguments[0] || {},
    i = 1,
    length = arguments.length,
    deep = false;

  // Handle a deep copy situation
  if (typeof target === "boolean") {
    deep = target;

    // Skip the boolean and the target
    target = arguments[i] || {};
    i++;
  }

  // Handle case when target is a string or something (possible in deep copy)
  if (typeof target !== "object" && !utils.isFunction(target)) {
    target = {};
  }

  // Extend utils itself if only one argument is passed
  if (i === length) {
    target = utils;
    i--;
  }

  for (; i < length; i++) {
    // Only deal with non-null/undefined values
    if ((options = arguments[i]) !== null) {
      // Extend the base object
      for (name in options) {
        src = target[name];
        copy = options[name];

        // Prevent never-ending loop
        if (target === copy) {
          continue;
        }

        // Recurse if we're merging plain objects or arrays
        if (
          deep &&
          copy &&
          (utils.isPlainObject(copy) || (copyIsArray = utils.isArray(copy)))
        ) {
          if (copyIsArray) {
            copyIsArray = false;
            clone = src && utils.isArray(src) ? src : [];
          } else {
            clone = src && utils.isPlainObject(src) ? src : {};
          }

          // Never move original objects, clone them
          target[name] = utils.extend(deep, clone, copy) as unknown;

          // Don't bring in undefined values
        } else if (copy !== undefined) {
          target[name] = copy;
        }
      }
    }
  }

  // Return the modified object
  return target;
};

utils.isArrayLike = function (obj: Dict) {
  // Support: iOS 8.2 (not reproducible in simulator)
  // `in` check used to prevent JIT error (gh-2145)
  // hasOwn isn't used here due to false negatives
  // regarding Nodelist length in IE
  const length = "length" in obj && obj.length,
    type = utils.type(obj);

  if (type === "function" || utils.isWindow(obj)) {
    return false;
  }

  if (obj.nodeType === 1 && length) {
    return true;
  }

  return (
    type === "array" ||
    length === 0 ||
    (typeof length === "number" && length > 0 && length - 1 in obj)
  );
} as ObjectUtils["isArrayLike"];

utils.type = function (obj) {
  if (obj === null) {
    return `${obj}`;
  }
  // Support: Android<4.0, iOS<6 (functionish RegExp)
  return typeof obj === "object" || typeof obj === "function"
    ? class2type[objectToString.call(obj)] || "object"
    : typeof obj;
};

utils.isFunction = function (obj): obj is (...args: unknown[]) => unknown {
  return utils.type(obj) === "function";
};

utils.isDate = function (obj): obj is Date {
  return utils.isPlainObject(obj) && utils.isFunction(obj.toUTCString);
};

utils.isArray = Array.isArray;

utils.isWindow = function (obj): obj is Window {
  return obj !== null && obj === (obj as Dict).window;
};

utils.isNumeric = function (obj) {
  // parseFloat NaNs numeric-cast false positives (null|true|false|"")
  // ...but misinterprets leading-number strings, particularly hex literals ("0x...")
  // subtraction forces infinities to NaN
  // adding 1 corrects loss of precision from parseFloat (#15100)
  return (
    !utils.isArray(obj) && (obj as number) - parseFloat(obj as string) + 1 >= 0
  );
};

utils.isString = function (obj): obj is string {
  return typeof obj === "string" || obj instanceof String;
};

utils.isPlainObject = function (obj: Dict): obj is Dict {
  // Not plain objects:
  // - Any object or value whose internal [[Class]] property is not "[object Object]"
  // - DOM nodes
  // - window
  if (utils.type(obj) !== "object" || obj.nodeType || utils.isWindow(obj)) {
    return false;
  }

  if (
    obj.constructor &&
    !hasOwn.call(obj.constructor.prototype, "isPrototypeOf")
  ) {
    return false;
  }

  // If the function hasn't returned already, we're confident that
  // |obj| is a plain object, created by {} or constructed with new Object
  return true;
} as ObjectUtils["isPlainObject"];

utils.isEmptyObject = function (obj) {
  for (const _name in obj) {
    return false;
  }
  return true;
};

// args is for internal usage only
utils.each = function <T extends ArrayLike<unknown>>(
  obj: T,
  callback: (this: unknown, ...args: unknown[]) => unknown,
  args?: unknown[],
): T {
  let value: unknown,
    i: number | string = 0,
    length = obj.length,
    isArray = utils.isArrayLike(obj);

  if (args) {
    if (isArray) {
      for (; i < length; i++) {
        value = callback.apply((obj as Dict)[i], args);

        if (value === false) {
          break;
        }
      }
    } else {
      for (i in obj) {
        value = callback.apply((obj as Dict)[i], args);

        if (value === false) {
          break;
        }
      }
    }

    // A special, fast, case for the most common use of each
  } else if (isArray) {
    for (; i < length; i++) {
      value = callback.call((obj as Dict)[i], i, (obj as Dict)[i]);

      if (value === false) {
        break;
      }
    }
  } else {
    for (i in obj) {
      value = callback.call((obj as Dict)[i], i, (obj as Dict)[i]);

      if (value === false) {
        break;
      }
    }
  }

  return obj;
};

utils.each(
  "Boolean Number String Function Array Date RegExp Object Error".split(" "),
  function (i, name) {
    class2type[`[object ${name}]`] = (name as string).toLowerCase();
  },
);
